/**
 * Internal payment paths carried through a flow as `?redirect=`, and the open-redirect
 * guard over them.
 *
 * Two flows use it. An operator can send a customer a link straight to a payment page,
 * which has to survive login (and verification). And the budget assessment is entered
 * from the payment page and has to hand the customer back to the plan they were
 * configuring, which is what `planDeepLink` builds and the two parsers read back.
 */

import { oneMonthAhead } from '@/lib/payment-schedule';

export const REDIRECT_PARAM = 'redirect';

const ACCOUNT_PAYMENT = '/account-payment';
const CASE_PAYMENT = '/payment/';

/**
 * A prefixed id, which is the only thing that can name a case: `make_prefixed_id` emits
 * `<prefix>_<hex>` and no id in the database holds a character outside this set, so it never
 * needs percent-encoding. Testing the shape rather than excluding one character at a time is
 * what makes this whole class — empty, nested, encoded, fragment-suffixed — fall out at once.
 */
const CASE_ID = /^[A-Za-z0-9_]+$/;

/**
 * Whether a path addresses one case's payment page.
 *
 * The route is `/payment/:debtId` with a required param, so anything the id cannot be is a
 * path that matches no route. Two things follow from accepting one: `exitTo` carries it, so
 * the customer lands on a dead page; and `BudgetAssessment` derives its scope from the same
 * string, so a value that no case id can equal silently discards the plan they configured.
 */
export function isCasePaymentPath(path: string): boolean {
  if (!path.startsWith(CASE_PAYMENT)) return false;
  return CASE_ID.test(path.slice(CASE_PAYMENT.length).split('?')[0]);
}

/**
 * Whether a path addresses the account-wide payment page.
 *
 * Exact, or followed by a query string — a prefix test would take `/account-paymentXYZ`,
 * which routes nowhere and which `BudgetAssessment` would still read as account scope,
 * building a plan over every case and then handing the customer to a dead page.
 *
 * Shared with the scope check rather than repeated, so the guard and the classifier cannot
 * disagree about what counts as the account page.
 */
export function isAccountPaymentPath(path: string): boolean {
  return path === ACCOUNT_PAYMENT || path.startsWith(`${ACCOUNT_PAYMENT}?`);
}

/**
 * Return the redirect target if it is a safe internal payment path, else null.
 * Rejects absolute URLs, protocol-relative (`//host`), traversal, and anything outside
 * the payment surfaces, so a crafted link cannot bounce the customer off-site.
 */
export function safeRedirectTarget(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith('//') || raw.includes('://')) return null;
  // Nothing here ever builds a fragment, and one appended after the query lands inside the
  // last parameter's value — `count=3#plan` reads back as no count at all and silently
  // restores a different plan length than the customer chose.
  if (raw.includes('#')) return null;
  if (!isCasePaymentPath(raw) && !isAccountPaymentPath(raw)) return null;
  if (/\.\.|%2e/i.test(raw)) return null;
  return raw;
}

/** The plan params `Payment.tsx` / `AccountPayment.tsx` capture from a deep link. */
export interface PlanDeepLink {
  /** `/payment/{caseId}` for one case, `/account-payment` for the whole account. */
  basePath: string;
  frequency: string;
  startDate: Date | undefined;
  count: number;
  /** Marks a return from the budget assessment, so the plan tab knows a confirmation is owed. */
  assessed?: boolean;
}

export function planDeepLink({ basePath, frequency, startDate, count, assessed }: PlanDeepLink): string {
  const params = new URLSearchParams({ tab: 'plan', frequency, count: String(count) });
  if (startDate) params.set('start', toDateParam(startDate));
  if (assessed) params.set('offer', 'assessed');
  return `${basePath}?${params}`;
}

function toDateParam(date: Date): string {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join(
    '-',
  );
}

/** Parse a deep-link start date (yyyy-MM-dd); undefined if absent/invalid. */
export function parsePlanStartDate(raw: string | null | undefined): Date | undefined {
  if (!raw) return undefined;
  const d = new Date(`${raw}T00:00:00`);
  if (Number.isNaN(d.getTime())) return undefined;
  // Clamp to the picker's allowed window [today, today + 1 month]; the link is
  // advisory, so a mistyped/out-of-range date snaps to the nearest valid bound.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const max = oneMonthAhead(today);
  if (d < today) return today;
  if (d > max) return max;
  return d;
}

/** Parse a deep-link instalment count; undefined if absent/invalid. Upper bounds are clamped
 *  by each tab's own `maxPayments` effect, which knows the configured tier. */
export function parsePlanCount(raw: string | null | undefined): number | undefined {
  if (!raw) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isInteger(n) && n >= 2 ? n : undefined;
}

/** Build a `?redirect=` query string for the given target (already a path). */
export function redirectQuery(target: string | null): string {
  return target ? `?${REDIRECT_PARAM}=${encodeURIComponent(target)}` : '';
}

/**
 * Whether a path's query has to survive login.
 *
 * Both payment surfaces read a landing (`tab`, `frequency`, `start`, `count`) on mount, and
 * an operator-sent link reaches them unauthenticated. An offer covering the whole account
 * has no case to name, so its link is account-scoped — dropping its landing would put the
 * customer on pay-in-full with no sign a tab had been asked for.
 *
 * `/payment-plan/` is deliberately absent: it shows a plan that already exists and reads no
 * landing.
 */
export function carriesPaymentLanding(pathname: string): boolean {
  return isCasePaymentPath(pathname) || isAccountPaymentPath(pathname);
}
